import { ParsedRecord } from 'src/components/import/ImportParser';
import {
    REGISTRATION_LEAD_EXCEPTION,
    REGISTRATION_LEAD_TIME,
    REGISTRATION_MEASUREMENT_METHOD,
    REGISTRATION_RESOURCE_TYPE,
    RESIDENTIAL_PROGRAM_TYPE,
} from '../values';
import { strikePrice, validateRegistrationDates } from '../validators';
import {
    locationNameHasOnlyAllowedCharacters,
    stringLengthInRange,
    isNotEmptyStringHandler,
    isOneOfHandler,
    isYesOrNoHandler,
    isDateHandlerWithTimezone,
    REGISTRATION_PROGRAM_FOR_IMPORT,
    REGISTRATION_PRODUCT_TYPE_FOR_IMPORT,
    isNumber,
    prettyPrintOptions,
} from './index';

export const pjmRegistrationCsvConfig = [
    {
        column: 'Location ID*',
        required: true,
        description: '',
        width: 250,
    },
    {
        column: 'Registration Name*',
        required: true,
        description: '',
        width: 250,
    },
    {
        column: 'Program*',
        required: true,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_PROGRAM_FOR_IMPORT)}`,
        width: 250,
    },
    {
        column: 'Effective Date (MM/DD/YYYY)*',
        required: true,
        description: 'Date in format MM/DD/YYYY, for example: 01/31/2025',
        width: 250,
    },
    {
        column: 'Termination Date (MM/DD/YYYY)*',
        required: true,
        description: 'Date in format MM/DD/YYYY, for example: 01/31/2025',
        width: 250,
    },
    {
        column: 'Allow Modification EDC (Yes/No)*',
        required: true,
        description: '',
        width: 200,
    },
    {
        column: 'Allow Modification LSE (Yes/No)*',
        required: true,
        description: '',
        width: 200,
    },
    {
        column: 'Measurement Method',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_MEASUREMENT_METHOD)}`,
        width: 200,
    },
    {
        column: 'Summer Managed Load (kW)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Winter Managed Load (kW)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Resource Type',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_RESOURCE_TYPE)}`,
        width: 200,
    },
    {
        column: 'Product',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_PRODUCT_TYPE_FOR_IMPORT)}`,
        width: 200,
    },
    {
        column: 'RPM DR Resource ID',
        required: false,
        description: 'Please find the correct ID on the DRHUB Reports page.',
        width: 200,
    },
    {
        column: 'Lead Time*',
        required: true,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_LEAD_TIME)}`,
        width: 100,
    },
    {
        column: 'Lead Exception Documentation (Yes/No)',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120"; Set Yes, if your company has necessary documentation to support the 30 minute notification exception in accordance with PJM tariff and rules`,
        width: 100,
    },
    {
        column: 'Lead Exception Reason',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120"; Is one of ${prettyPrintOptions(REGISTRATION_LEAD_EXCEPTION)}`,
        width: 100,
    },
    {
        column: 'Lead Exception Description',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120";`,
        width: 100,
    },
    {
        column: 'Summer Only (Yes/No)*',
        required: true,
        description:
            'Yes - means the user confirms this registration is being used to fulfill a Summer Only RPM or FRR Commitment and is required to test if not required to respond to dispatch in the summer.',
        width: 200,
    },
    {
        column: 'Strike Price (US$)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Shutdown Cost (US$)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Residential Registration (Yes/No)',
        required: false,
        description: '',
        width: 200,
    },
    {
        column: 'Residential Program Type',
        required: false,
        description: `Provide only if "Residential Registration (Yes/No)" is "Yes"; Is one of ${prettyPrintOptions(RESIDENTIAL_PROGRAM_TYPE)}`,
        width: 100,
    },
    {
        column: 'Load Data Sample (Yes/No)',
        required: false,
        description: 'Provide only if "Residential Registration (Yes/No)" is "Yes"',
        width: 200,
    },
    {
        column: '# Sample Participants',
        required: false,
        description: 'Provide only if "Residential Registration (Yes/No)" is "Yes"',
        width: 200,
    },
] as const; // tricky - but it needs to properly infer PjmRegistrationCsvColumns type bellow

export type PjmRegistrationCsvColumns = (typeof pjmRegistrationCsvConfig)[number]['column'];

export function pjmRegistrationParser(row: ParsedRecord<PjmRegistrationCsvColumns>): ParsedRecord<PjmRegistrationCsvColumns> {
    row = isNumber<PjmRegistrationCsvColumns>(row, 'Location ID*');
    row = isNotEmptyStringHandler<PjmRegistrationCsvColumns>(row, 'Registration Name*', [
        locationNameHasOnlyAllowedCharacters,
        stringLengthInRange(0, 40),
    ]);
    row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Program*', REGISTRATION_PROGRAM_FOR_IMPORT);
    row = isDateHandlerWithTimezone<PjmRegistrationCsvColumns>(row, 'Effective Date (MM/DD/YYYY)*', 'start');
    row = isDateHandlerWithTimezone<PjmRegistrationCsvColumns>(row, 'Termination Date (MM/DD/YYYY)*', 'end');

    if (row['Program*'].parsedValue) {
        const startDate = new Date(row['Effective Date (MM/DD/YYYY)*'].parsedValue);
        const endDate = new Date(row['Termination Date (MM/DD/YYYY)*'].parsedValue);
        const res = validateRegistrationDates(row['Program*'].parsedValue, startDate, endDate);
        if (!res.valid) {
            row['Effective Date (MM/DD/YYYY)*'].error = res.error?.message;
        }
    }

    row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Allow Modification EDC (Yes/No)*');
    row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Allow Modification LSE (Yes/No)*');

    if (row['Measurement Method'].value !== '') {
        row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Measurement Method', REGISTRATION_MEASUREMENT_METHOD);
    }

    if (row['Summer Managed Load (kW)'].value !== '') {
        row = isNumber<PjmRegistrationCsvColumns>(row, 'Summer Managed Load (kW)');
    }

    if (row['Winter Managed Load (kW)'].value !== '') {
        row = isNumber<PjmRegistrationCsvColumns>(row, 'Winter Managed Load (kW)');
    }

    if (row['Resource Type'].value !== '') {
        row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Resource Type', REGISTRATION_RESOURCE_TYPE);
    }

    if (row['Product'].value !== '') {
        row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Product', REGISTRATION_PRODUCT_TYPE_FOR_IMPORT);
    }

    // RPM DR Resource - optional
    // ! DRHUB expects number (ID) here but we don't know how to get this number (by API)
    // ! so we relay on a user - they can visit DRHUB Reports page, and find correct ID by RPM DR Resource name
    // ! and place the ID here (in the imported file)
    if (row['RPM DR Resource ID'].value !== '') {
        row = isNumber<PjmRegistrationCsvColumns>(row, 'RPM DR Resource ID');
    }

    row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Lead Time*', REGISTRATION_LEAD_TIME);

    if (row['Lead Exception Documentation (Yes/No)'].value !== '') {
        row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Lead Exception Documentation (Yes/No)');
    }

    if (row['Lead Exception Reason'].value !== '') {
        row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Lead Exception Reason', REGISTRATION_LEAD_EXCEPTION);
    }
    row['Lead Exception Description'].parsedValue = row['Lead Exception Description'].value;

    row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Summer Only (Yes/No)*');

    if (row['Strike Price (US$)'].value !== '') {
        row = isNumber<PjmRegistrationCsvColumns>(row, 'Strike Price (US$)');
    }

    if (row['Shutdown Cost (US$)'].value !== '') {
        row = isNumber<PjmRegistrationCsvColumns>(row, 'Shutdown Cost (US$)');
    }

    if (row['Strike Price (US$)'].parsedValue && row['Lead Time*'].parsedValue) {
        const result = strikePrice.isValid(row['Strike Price (US$)'].parsedValue, row['Lead Time*'].parsedValue);
        if (!result.valid) {
            row['Strike Price (US$)'].error = result.error.message;
        }
    }

    if (row['Residential Registration (Yes/No)'].value) {
        row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Residential Registration (Yes/No)');
    }

    if (row['Residential Registration (Yes/No)'].parsedValue === true) {
        if (row['Residential Program Type'].value) {
            row = isOneOfHandler<PjmRegistrationCsvColumns>(row, 'Residential Program Type', RESIDENTIAL_PROGRAM_TYPE);
        }

        if (row['Load Data Sample (Yes/No)'].value) {
            row = isYesOrNoHandler<PjmRegistrationCsvColumns>(row, 'Load Data Sample (Yes/No)');
        }

        if (row['# Sample Participants'].value) {
            row = isNumber<PjmRegistrationCsvColumns>(row, '# Sample Participants');
        }
    }

    return row;
}
